export type CarouselPublishPlatform = 'instagram' | 'tiktok' | 'facebook';

export type CarouselPublishWhen =
  | { mode: 'now' }
  | { mode: 'later'; scheduledAt: string };

export interface CarouselTikTokPostInfo {
  privacyLevel: string;
  disableComment: boolean;
  brandContentToggle: boolean;
  brandOrganicToggle: boolean;
}

export type CarouselPublishInput =
  | {
    kind: 'instagram_now';
    variables: {
      clipId: string;
      socialConnectionId: string;
      caption: string;
      imageUrls: string[];
    };
  }
  | {
    kind: 'facebook_now';
    variables: {
      clipId: string;
      socialConnectionId: string;
      caption: string;
      imageUrls: string[];
    };
  }
  | {
    kind: 'schedule';
    variables: {
      input: {
        clipId: string;
        socialConnectionId: string;
        platform: 'instagram' | 'facebook';
        caption: string;
        scheduledAt: string;
        imageUrls: string[];
      };
    };
  }
  | {
    kind: 'tiktok_now';
    variables: {
      clipId: string;
      socialConnectionId: string;
      title: string;
      privacyLevel: string;
      disableComment: boolean;
      brandContentToggle: boolean;
      brandOrganicToggle: boolean;
      imageUrls: string[];
    };
  };

/**
 * Picks the mutation and variables for publishing a rendered carousel. The
 * carousel rides on the existing publish mutations via `imageUrls`:
 * Instagram now → `publishClipToInstagram`, Instagram later →
 * `createMyScheduledPublish`, Facebook now → `publishClipToFacebook` (later →
 * the same schedule input with `platform: 'facebook'`), TikTok now → `publishClipToTikTok` (Direct
 * Post). For TikTok the full caption goes in `title`: the backend puts it in
 * the photo post description and derives the short photo title from it.
 * TikTok carousels cannot be scheduled (throws `carousel_unsupported`).
 */
export function buildCarouselPublishInput(args: {
  platform: CarouselPublishPlatform;
  clipId: string;
  socialConnectionId: string;
  imageUrls: string[];
  caption: string;
  when: CarouselPublishWhen;
  tiktok?: CarouselTikTokPostInfo;
}): CarouselPublishInput {
  const {
    platform, clipId, socialConnectionId, caption, when, tiktok,
  } = args;
  const imageUrls = [...args.imageUrls];

  if (platform === 'tiktok') {
    if (when.mode !== 'now' || !tiktok) throw new Error('carousel_unsupported');
    return {
      kind: 'tiktok_now',
      variables: {
        clipId,
        socialConnectionId,
        title: caption,
        privacyLevel: tiktok.privacyLevel,
        disableComment: tiktok.disableComment,
        brandContentToggle: tiktok.brandContentToggle,
        brandOrganicToggle: tiktok.brandOrganicToggle,
        imageUrls,
      },
    };
  }

  if (when.mode === 'later') {
    return {
      kind: 'schedule',
      variables: {
        input: {
          clipId,
          socialConnectionId,
          platform,
          caption,
          scheduledAt: when.scheduledAt,
          imageUrls,
        },
      },
    };
  }

  if (platform === 'facebook') {
    return {
      kind: 'facebook_now',
      variables: {
        clipId, socialConnectionId, caption, imageUrls,
      },
    };
  }

  return {
    kind: 'instagram_now',
    variables: {
      clipId, socialConnectionId, caption, imageUrls,
    },
  };
}
